import { Field, Form, Formik } from 'formik';
import React from 'react';
import styled from 'styled-components';
import { PrimaryButton } from '../../../../../../CommonComponents/Button';
import FormikInput from '../../../../../../CommonComponents/FormikInput';
import Icon from '../../../../../../CommonComponents/Icon';
import Select from '../../../../../../CommonComponents/Select';
import { AssetClass } from '../../../types';
import { BottomNavigation, SectionTitle, StepBody, StepContent } from '../styles';

const categoryOptions = [
  { label: 'Stocks', value: 0 },
  { label: 'Bonds', value: 1 },
  { label: 'Cash', value: 2 },
  { label: 'Alternatives', value: 4 },
];

const Table = styled.table`
  border-spacing: 12px 24px;
  border-collapse: separate;
  width: 100%;
`;

const NameCell = styled.td`
  width: 34%;
`;

const CategoryCell = styled.td`
  width: 40%;
`;

const EmptyTitle = styled.div`
  font-weight: 600;
  font-size: 32px;
  color: #444444;
`;

const EmptySubtitle = styled.div`
  font-weight: 600;
  line-height: 30px;
  color: #767676;
`;

const EmptyWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${Icon} {
    font-size: 90px;
    color: #cbc9c9;
    margin-bottom: 32px;
  }
`;

interface NameCustomAssetClassesProps {
  assetClasses: AssetClass[];
  next: (assetClasses: AssetClass[]) => void;
}

const EmptyNameCustomAssetClasses: React.FC = () => (
  <EmptyWrapper>
    <Icon className="fas fa-rocket" />

    <EmptyTitle>There are no custom asset classes.</EmptyTitle>
    <EmptySubtitle>Go ahead and jump to the next step.</EmptySubtitle>
  </EmptyWrapper>
);

export const NameCustomAssetClasses: React.FC<NameCustomAssetClassesProps> = ({
  assetClasses,
  next,
}) => {
  const hasCustomAssetClasses = assetClasses.some(({ included, isCustom }) => included && isCustom);

  const initialValues = {
    assetClasses: assetClasses.map(assetClass => ({
      ...assetClass,
      customName: assetClass.customName === assetClass.originalName ? '' : assetClass.customName,
    })),
  };

  return (
    <StepBody>
      <Formik
        onSubmit={({ assetClasses: updatedAssetClasses }) =>
          next(
            updatedAssetClasses.map(assetClass => ({
              ...assetClass,
              customName: assetClass.customName || assetClass.originalName,
            })),
          )
        }
        initialValues={initialValues}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <StepContent>
              {hasCustomAssetClasses ? (
                <>
                  <SectionTitle>Custom Asset Classes</SectionTitle>

                  <Table>
                    <tbody>
                      {assetClasses.map(
                        ({ id, originalName, included, isCustom }, i) =>
                          included &&
                          isCustom && (
                            <tr key={id}>
                              <td>
                                <label htmlFor={id.toString()}>{originalName}</label>
                              </td>
                              <NameCell>
                                <Field
                                  component={FormikInput}
                                  id={id}
                                  name={`assetClasses[${i}].customName`}
                                  placeholder={originalName}
                                />
                              </NameCell>
                              <CategoryCell>
                                <Select
                                  placeholder="Select Asset Category"
                                  options={categoryOptions}
                                  value={categoryOptions.find(
                                    ({ value }) =>
                                      value === values.assetClasses[i].customHighLevelAssetClass,
                                  )}
                                  onChange={option =>
                                    setFieldValue(
                                      `assetClasses[${i}].customHighLevelAssetClass`,
                                      option.value,
                                    )
                                  }
                                />
                              </CategoryCell>
                            </tr>
                          ),
                      )}
                    </tbody>
                  </Table>
                </>
              ) : (
                <EmptyNameCustomAssetClasses />
              )}
            </StepContent>
            <BottomNavigation>
              <PrimaryButton type="submit">Next</PrimaryButton>
            </BottomNavigation>
          </Form>
        )}
      </Formik>
    </StepBody>
  );
};
